"use client";

import { useState } from "react";
import {
  Calendar,
  Sparkles,
  ImageIcon,
  Maximize2,
  Pencil,
  Wand2,
  ChevronLeft,
  ChevronRight,
  Sun,
  Leaf,
  MapPin,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { BeforeAfterSlider } from "@/components/shared/BeforeAfterSlider";
import { ImageFullScreenViewer } from "@/components/shared/ImageFullScreenViewer";
import { AiInspirationDisclaimer } from "@/components/shared/AiInspirationDisclaimer";
import { EditPanel, EditVersion, PlantReconciliationResult } from "./EditPanel";

interface GenerationResults {
  // summer is the always-generated baseline/hero; spring only exists in
  // all-seasons mode, so it is optional.
  summer: string;
  spring?: string;
  autumn: string;
  winter: string;
  gif: string;
  diagramUrl?: string;
}

export type SeasonEditHistories = Record<string, EditVersion[]>;

interface ResultViewerProps {
  results: GenerationResults;
  originalImageUrl: string;
  initialEditHistories?: SeasonEditHistories;
  initialEditedResults?: Partial<GenerationResults>;
  onResultsUpdate?: (season: string, newImageUrl: string) => void;
  onEditHistoriesChange?: (histories: SeasonEditHistories) => void;
  onEditedResultsChange?: (editedResults: Partial<GenerationResults>) => void;
  sunLevel?: string;
  styleName?: string;
  zone?: string;
  // PLA2-548: DB-linked identifiers needed to opt an edit into the
  // plant-aware reconciliation path (see EditPanel). Omit either to keep
  // the existing DB-free edit behavior unchanged.
  projectId?: string;
  seasonGenerationIds?: Partial<Record<ViewMode, string>>;
  additionalGenusAliasIds?: string[];
  countPlants?: boolean;
  generateDiagram?: boolean;
  /** PLA2-549/550/551/556: LIVE reconciliation on the consumer edit path
   *  (no designer2 Generation id needed). The design's currently selected
   *  plant ids and their current shopping-list quantities — ResultViewer
   *  only turns reconciliation on when editing the SUMMER view (dormant
   *  plants in other seasons would false-positive as "removed"). */
  candidatePlantIds?: string[];
  priorCounts?: Record<string, number>;
  /** Fired when an edit's response carries plantCounts/plantChanges/diagramUrl
   *  — the caller should reconcile its shopping list / cart against the new counts. */
  onPlantChanges?: (result: PlantReconciliationResult) => void;
}

function formatSunLevel(sunLevel: string) {
  return sunLevel
    .replace(/_/g, " ")
    .replace(/part sun part shade/i, "Part Sun")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatZone(zone: string) {
  return zone
    .replace(/^ZONE_/i, "Zone ")
    .replace(/([A-Z])$/, (m) => m.toLowerCase());
}

type ViewMode = "spring" | "summer" | "autumn" | "winter" | "gif";

export function ResultViewer({
  results,
  originalImageUrl,
  initialEditHistories,
  initialEditedResults,
  onResultsUpdate,
  onEditHistoriesChange,
  onEditedResultsChange,
  sunLevel,
  styleName,
  zone,
  projectId,
  seasonGenerationIds,
  additionalGenusAliasIds,
  countPlants,
  generateDiagram,
  candidatePlantIds,
  priorCounts,
  onPlantChanges,
}: ResultViewerProps) {
  const firstAvailableView: ViewMode =
    (["summer", "autumn", "winter", "spring", "gif"] as ViewMode[]).find((k) =>
      Boolean(results[k as keyof GenerationResults])
    ) ?? "summer";
  const [activeView, setActiveView] = useState<ViewMode>(firstAvailableView);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedResults, setEditedResults] = useState<
    Partial<GenerationResults>
  >(initialEditedResults || {});
  // Per-season edit histories
  const [editHistories, setEditHistories] = useState<SeasonEditHistories>(
    initialEditHistories || {}
  );
  // Tracks whichever version the user selects in the edit history (null = use latest)
  const [editPreviewUrl, setEditPreviewUrl] = useState<string | null>(null);

  const currentResults = { ...results, ...editedResults };

  const editableSeasons: ViewMode[] = ["summer", "autumn", "winter", "spring"];
  const editedSeasons = editableSeasons.filter(
    (s) => editedResults[s as keyof GenerationResults]
  );
  const hasAnyEdits = editedSeasons.length > 0;
  const totalGeneratedSeasons = editableSeasons.filter(
    (s) => results[s as keyof GenerationResults]
  ).length;
  const isSeasonEdited = (key: ViewMode) =>
    editableSeasons.includes(key) &&
    Boolean(editedResults[key as keyof GenerationResults]);
  const seasonVersionCount = (key: ViewMode) => {
    const hist = editHistories[key]?.length || 0;
    // History stores each new version; +1 for the original
    return hist > 0 ? hist + 1 : isSeasonEdited(key) ? 2 : 1;
  };

  const allViews: {
    key: ViewMode;
    label: string;
    icon: any;
    url: string | undefined;
  }[] = [
    {
      key: "summer",
      label: "Summer",
      icon: ImageIcon,
      url: currentResults.summer,
    },
    {
      key: "autumn",
      label: "Autumn",
      icon: Calendar,
      url: currentResults.autumn,
    },
    {
      key: "winter",
      label: "Winter",
      icon: ImageIcon,
      url: currentResults.winter,
    },
    {
      key: "spring",
      label: "Spring",
      icon: Sparkles,
      url: currentResults.spring,
    },
    { key: "gif", label: "Timelapse", icon: Sparkles, url: currentResults.gif },
  ];

  // The planting diagram is not a hero view: the before/after slider below
  // compares the hero against the original photo, which means nothing for a
  // diagram. It is shown side by side with the garden in PlantingGuideCard.

  // Filter out views with no URL (e.g. when only summer was generated)
  const views = allViews.filter((v) => v.url);

  const seasonUrl =
    views.find((v) => v.key === activeView)?.url || views[0]?.url || "";

  // When editing, show the selected history version; otherwise show the season's latest
  const activeUrl = isEditing && editPreviewUrl ? editPreviewUrl : seasonUrl;

  // Only allow editing seasonal images, not the gif
  const isEditableSeason = ["summer", "autumn", "winter", "spring"].includes(
    activeView
  );

  // Resolve the TRUE original URL for a season. The parent mutates `results`
  // with each edit (via onResultsUpdate), so `results[season]` is NOT reliable
  // as the original once an edit has happened. EditPanel always seeds history[0]
  // with the original, so that's our source of truth when history exists.
  const originalUrlOf = (key: ViewMode) => {
    const history = editHistories[key];
    if (history && history.length > 0) return history[0].imageUrl;
    return results[key as keyof GenerationResults] as string | undefined;
  };

  // Is the user currently viewing the ORIGINAL (unedited) version of this season?
  // True when the main canvas shows the original image generated for this season.
  const viewingOriginalOfActive =
    isEditableSeason &&
    activeUrl !== undefined &&
    activeUrl === originalUrlOf(activeView);

  // While the user is previewing the Original in edit mode, treat the whole page
  // as if no edits are currently "in view" — so unedited seasons + GIF shouldn't
  // be tagged as stale. When the user switches to an edited version, restore
  // the real edited-state so the others visibly fall out of sync.
  const displayAsEdited = !(isEditing && viewingOriginalOfActive);
  const visibleEditedSeasons = displayAsEdited ? editedSeasons : [];
  const visibleHasEdits = visibleEditedSeasons.length > 0;
  const isSeasonShownEdited = (key: ViewMode) =>
    displayAsEdited && isSeasonEdited(key);
  // GIF is always "the original timelapse" — once any season is edited, it's out of sync
  const gifIsStale = visibleHasEdits;

  const handleEditComplete = (newImageUrl: string) => {
    const newEditedResults = { ...editedResults, [activeView]: newImageUrl };
    setEditedResults(newEditedResults);
    setEditPreviewUrl(newImageUrl);
    onResultsUpdate?.(activeView, newImageUrl);
    onEditedResultsChange?.(newEditedResults);
  };

  const handleVersionSelect = (imageUrl: string) => {
    setEditPreviewUrl(imageUrl);
  };

  const handleHistoryChange = (history: EditVersion[]) => {
    const newHistories = { ...editHistories, [activeView]: history };
    setEditHistories(newHistories);
    onEditHistoriesChange?.(newHistories);
  };

  // PLA2-548/556: when the edit response carries a regenerated diagram,
  // merge it into editedResults so the "Planting Guide" tab reflects the
  // edited design — same mechanism as a season's edited image.
  const handlePlantChanges = (result: PlantReconciliationResult) => {
    if (result.diagramUrl) {
      const newEditedResults = {
        ...editedResults,
        diagramUrl: result.diagramUrl,
      };
      setEditedResults(newEditedResults);
      onEditedResultsChange?.(newEditedResults);
    }
    onPlantChanges?.(result);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-plantista-950 dark:text-white tracking-tight font-display">
            Your Garden Transformation
          </h2>
          <p className="text-plantista-600 dark:text-plantista-300 mt-1">
            {views.length > 1
              ? "AI-powered seasonal visualizations of your new space"
              : "AI-powered visualization of your new space"}
          </p>
          {(sunLevel || styleName || zone) && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {sunLevel && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                  <Sun className="w-3.5 h-3.5" />
                  {formatSunLevel(sunLevel)}
                </span>
              )}
              {styleName && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-plantista-50 dark:bg-plantista-900 text-plantista-700 dark:text-plantista-300 border border-plantista-200 dark:border-plantista-700">
                  <Leaf className="w-3.5 h-3.5" />
                  {styleName}
                </span>
              )}
              {zone && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                  <MapPin className="w-3.5 h-3.5" />
                  {formatZone(zone)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Display Area — hidden while editing; the EditPanel (which owns its
          own hero-sized drawable canvas) takes over as the top element. */}
      {!(isEditing && isEditableSeason) && (
        <div className="relative group rounded-3xl overflow-hidden shadow-2xl bg-gray-100 dark:bg-plantista-950 aspect-video md:aspect-[16/9] border-4 border-gray-200 dark:border-plantista-800">
          {activeUrl && (
            <img
              src={activeUrl}
              alt={`Seasonal view: ${activeView}`}
              className="w-full h-full object-contain transition-opacity duration-500 cursor-pointer"
              onClick={() => setFullScreenImage(activeUrl)}
            />
          )}

          {/* Overlay for Season Label */}
          <div className="absolute top-6 left-6 flex items-center gap-2">
            <div className="px-4 py-2 bg-black/60 backdrop-blur-md rounded-full border border-white/10 text-white text-sm font-bold flex items-center gap-2">
              {activeView === "gif" ? (
                <Sparkles className="w-4 h-4 text-plantista-300" />
              ) : (
                <Calendar className="w-4 h-4 text-plantista-300" />
              )}
              <span className="capitalize">
                {activeView === "gif"
                  ? "All Seasons"
                  : `${activeView} · ${views.findIndex((v) => v.key === activeView) + 1} of ${views.length}`}
              </span>
            </div>
            {isEditableSeason && isSeasonShownEdited(activeView) && (
              <div className="px-3 py-1.5 bg-emerald-500/90 backdrop-blur-md rounded-full border border-emerald-300/40 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg">
                <CheckCircle2 className="w-3.5 h-3.5" />v
                {seasonVersionCount(activeView)}
              </div>
            )}
            {isEditableSeason &&
              !isSeasonShownEdited(activeView) &&
              visibleHasEdits && (
                <div className="px-3 py-1.5 bg-amber-500/90 backdrop-blur-md rounded-full border border-amber-300/40 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Original
                </div>
              )}
            {activeView === "gif" && gifIsStale && (
              <div className="px-3 py-1.5 bg-amber-500/90 backdrop-blur-md rounded-full border border-amber-300/40 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg">
                <AlertCircle className="w-3.5 h-3.5" />
                Original
              </div>
            )}
          </div>

          {/* Progress badge — shows updated count once editing starts */}
          {totalGeneratedSeasons > 1 && (
            <div className="absolute top-6 left-1/2 -translate-x-1/2">
              <div className="px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-white/10 text-white text-xs font-bold flex items-center gap-1.5">
                <Sparkles
                  className={`w-3 h-3 ${visibleHasEdits ? "text-emerald-300" : "text-neon-green"}`}
                />
                {visibleHasEdits
                  ? `${visibleEditedSeasons.length} of ${totalGeneratedSeasons} updated`
                  : `${totalGeneratedSeasons} Seasons`}
              </div>
            </div>
          )}

          {/* Top right buttons */}
          <div className="absolute top-6 right-6 flex items-center gap-2">
            {/* Edit Button */}
            {isEditableSeason && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`px-4 py-3 backdrop-blur-md rounded-full border transition-all flex items-center gap-2 text-sm font-bold shadow-lg ${
                  isEditing
                    ? "bg-plantista-500/90 border-plantista-400/50 text-white"
                    : "bg-neon-green border-neon-green text-plantista-950 hover:shadow-xl hover:shadow-neon-green/30 hover:-translate-y-0.5"
                }`}
                title="Edit this image with AI"
              >
                <Pencil className="w-4 h-4" />
                <span>{isEditing ? "Editing" : "Edit"}</span>
              </button>
            )}

            {/* Full Screen Button */}
            <button
              onClick={() => setFullScreenImage(activeUrl)}
              className="p-3 bg-black/60 backdrop-blur-md rounded-full border border-white/10 text-white hover:bg-black/80 transition-colors"
              aria-label="View full screen"
              title="View Full Screen"
            >
              <Maximize2 className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          {/* Left arrow */}
          {views.length > 1 && (
            <button
              onClick={() => {
                const idx = views.findIndex((v) => v.key === activeView);
                const prev = views[(idx - 1 + views.length) % views.length];
                setActiveView(prev.key);
                setEditPreviewUrl(null);
                setIsEditing(false);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 backdrop-blur-md rounded-full border border-white/20 text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Right arrow */}
          {views.length > 1 && (
            <button
              onClick={() => {
                const idx = views.findIndex((v) => v.key === activeView);
                const next = views[(idx + 1) % views.length];
                setActiveView(next.key);
                setEditPreviewUrl(null);
                setIsEditing(false);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 backdrop-blur-md rounded-full border border-white/20 text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Dot indicators */}
          {views.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {views.map((v) => (
                <button
                  key={v.key}
                  onClick={() => {
                    setActiveView(v.key);
                    setEditPreviewUrl(null);
                    setIsEditing(false);
                  }}
                  aria-label={v.label}
                  className={`rounded-full transition-all duration-300 ${
                    activeView === v.key
                      ? "w-5 h-2 bg-white shadow-md"
                      : "w-2 h-2 bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stale-season notice — only when viewing an unedited season after edits exist */}
      {visibleHasEdits &&
        (isEditableSeason
          ? !isSeasonShownEdited(activeView) && !isEditing
          : activeView === "gif") && (
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 animate-fade-in">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/60 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900 dark:text-amber-200 capitalize">
                {activeView === "gif"
                  ? "Timelapse still shows your original design"
                  : `${activeView} still shows your original design`}
              </p>
              <p className="text-sm text-amber-800/90 dark:text-amber-300/90 mt-0.5">
                You&apos;ve updated{" "}
                {visibleEditedSeasons.length === 1
                  ? visibleEditedSeasons[0]
                  : `${visibleEditedSeasons.length} other seasons`}
                .
                {activeView === "gif"
                  ? " The timelapse will regenerate once you update all seasons."
                  : " This season hasn't been regenerated with your edits yet."}
              </p>
            </div>
            {activeView !== "gif" && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit this season
              </button>
            )}
          </div>
        )}

      {/* Edit feature discovery — prominent, compact entry point.
          Hidden while editing, and while the amber stale-notice (which has its
          own edit CTA) is showing for an unedited season. */}
      {isEditableSeason &&
        !isEditing &&
        !(visibleHasEdits && !isSeasonShownEdited(activeView)) && (
          <button
            onClick={() => setIsEditing(true)}
            className="group/cta w-full flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl text-left transition-all duration-300 border-2 border-plantista-200 dark:border-plantista-800 bg-gradient-to-r from-plantista-50 to-white dark:from-plantista-900 dark:to-plantista-950 hover:border-neon-green hover:shadow-lg hover:shadow-neon-green/10 animate-fade-in"
          >
            <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-neon-green flex items-center justify-center shadow-md transition-transform group-hover/cta:scale-105">
              <Wand2 className="w-5 h-5 sm:w-6 sm:h-6 text-plantista-950" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-bold text-plantista-950 dark:text-white flex items-center gap-2">
                {isSeasonShownEdited(activeView)
                  ? "Keep refining"
                  : "Make it yours"}
                <span className="px-1.5 py-0.5 rounded-md bg-neon-green/25 text-plantista-700 dark:text-neon-green text-[10px] font-bold uppercase tracking-wide">
                  AI Edit
                </span>
              </p>
              <p className="text-xs sm:text-sm text-plantista-600 dark:text-plantista-300 mt-0.5">
                Swap plants, adjust colors, or refine any detail of your{" "}
                <span className="capitalize">{activeView}</span> design.
              </p>
            </div>
            <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-2.5 rounded-xl bg-plantista-950 dark:bg-white text-white dark:text-plantista-950 text-sm font-bold transition-colors group-hover/cta:bg-neon-green group-hover/cta:text-plantista-950">
              <Pencil className="w-4 h-4" />
              <span className="hidden sm:inline">Edit</span>
            </span>
          </button>
        )}

      {/* Edit Panel (slides in below main image) */}
      {isEditing && isEditableSeason && (
        <EditPanel
          imageUrl={seasonUrl}
          season={activeView.charAt(0).toUpperCase() + activeView.slice(1)}
          initialHistory={editHistories[activeView]}
          onEditComplete={handleEditComplete}
          onVersionSelect={handleVersionSelect}
          onHistoryChange={handleHistoryChange}
          onClose={() => {
            setIsEditing(false);
            setEditPreviewUrl(null);
          }}
          projectId={projectId}
          parentGenerationId={seasonGenerationIds?.[activeView]}
          additionalGenusAliasIds={additionalGenusAliasIds}
          countPlants={countPlants}
          generateDiagram={generateDiagram}
          // PLA2-549/550/551/556: only reconcile when editing the SUMMER
          // view — winter/autumn dormancy would false-positive as "removed".
          reconcilePlants={Boolean(countPlants) && activeView === "summer"}
          candidatePlantIds={candidatePlantIds}
          priorCounts={priorCounts}
          onPlantChanges={handlePlantChanges}
        />
      )}

      {/* Navigation Thumbnails */}
      {views.length > 1 && (
        <div
          className={`grid gap-4 ${
            views.length === 1
              ? "grid-cols-1"
              : views.length === 2
                ? "grid-cols-2"
                : views.length === 3
                  ? "grid-cols-3"
                  : "grid-cols-3 md:grid-cols-5"
          }`}
        >
          {views.map((v) => {
            const isEditableThumb = editableSeasons.includes(v.key);
            const edited = isEditableThumb && isSeasonShownEdited(v.key);
            const isGifThumb = v.key === "gif";
            const stale =
              (isEditableThumb && !edited && visibleHasEdits) ||
              (isGifThumb && gifIsStale);
            const isActive = activeView === v.key;
            return (
              <button
                key={v.key}
                onClick={() => {
                  setActiveView(v.key);
                  setEditPreviewUrl(null);
                  setIsEditing(false);
                }}
                className={`group relative flex flex-col items-center gap-3 p-4 rounded-2xl transition-all duration-300 ${
                  isActive
                    ? edited
                      ? "bg-emerald-50 dark:bg-emerald-950/40 ring-2 ring-emerald-500 dark:ring-emerald-400"
                      : stale
                        ? "bg-amber-50 dark:bg-amber-950/30 ring-2 ring-amber-500 dark:ring-amber-400"
                        : "bg-plantista-100 dark:bg-plantista-800 ring-2 ring-plantista-500 dark:ring-plantista-400"
                    : "bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 hover:bg-gray-50 dark:hover:bg-plantista-800 hover:shadow-md"
                }`}
              >
                <div
                  className={`w-full aspect-square rounded-xl overflow-hidden border-2 relative ${
                    isActive
                      ? edited
                        ? "border-emerald-500 dark:border-emerald-400"
                        : stale
                          ? "border-amber-500 dark:border-amber-400"
                          : "border-plantista-500 dark:border-plantista-400"
                      : "border-transparent"
                  }`}
                >
                  {v.url && (
                    <img
                      src={v.url}
                      alt={v.label}
                      className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-500 ${
                        stale ? "opacity-60 saturate-50" : ""
                      }`}
                    />
                  )}
                  {stale && (
                    <div className="absolute inset-0 bg-gradient-to-t from-amber-900/30 via-transparent to-transparent pointer-events-none" />
                  )}
                  {/* Status chip in the corner of the image */}
                  {edited && (
                    <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center gap-1 shadow-md">
                      <CheckCircle2 className="w-3 h-3" />v
                      {seasonVersionCount(v.key)}
                    </div>
                  )}
                  {stale && (
                    <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-amber-500/95 text-white text-[10px] font-bold flex items-center gap-1 shadow-md">
                      <AlertCircle className="w-3 h-3" />
                      Original
                    </div>
                  )}
                </div>
                <span
                  className={`text-sm font-bold ${
                    isActive
                      ? edited
                        ? "text-emerald-900 dark:text-emerald-100"
                        : stale
                          ? "text-amber-900 dark:text-amber-100"
                          : "text-plantista-900 dark:text-white"
                      : stale
                        ? "text-amber-700/80 dark:text-amber-400/80"
                        : "text-gray-500 dark:text-plantista-400"
                  }`}
                >
                  {v.label}
                </span>
                {isActive && (
                  <div
                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center ${
                      edited
                        ? "bg-emerald-500 dark:bg-emerald-400"
                        : stale
                          ? "bg-amber-500 dark:bg-amber-400"
                          : "bg-plantista-500 dark:bg-plantista-400"
                    }`}
                  >
                    <div className="w-1.5 h-1.5 bg-white dark:bg-plantista-950 rounded-full" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Comparison Divider - Interactive Before/After Slider */}
      {originalImageUrl && activeUrl && (
        <div className="pt-8 border-t border-gray-200 dark:border-plantista-800">
          <h3 className="text-xl font-bold text-plantista-950 dark:text-white mb-6 flex items-center gap-2 font-display">
            <ImageIcon className="w-5 h-5 text-plantista-600 dark:text-plantista-400" />
            Interactive Before & After Comparison
          </h3>
          <BeforeAfterSlider
            beforeImage={originalImageUrl}
            afterImage={activeUrl}
            beforeAlt="Original Space"
            afterAlt={`New Vision (${activeView.charAt(0).toUpperCase() + activeView.slice(1)})`}
          />
        </div>
      )}

      {/* AI-for-inspiration note — design surface (PLA2-338). The
          horticultural / spacing / 811 / HOA disclaimer is rendered by the
          design page below the "Make it Happen" CTA, not here. */}
      <AiInspirationDisclaimer className="mt-6" />

      {/* Full Screen Viewer Modal */}
      <ImageFullScreenViewer
        imageUrl={fullScreenImage || ""}
        alt={`${activeView} view`}
        isOpen={!!fullScreenImage}
        onClose={() => setFullScreenImage(null)}
      />
    </div>
  );
}
